import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage to prevent flash of unauthenticated state
    const savedUser = localStorage.getItem('reddi_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize auth state based on token presence
    return !!localStorage.getItem('reddi_token');
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('reddi_token');
    const savedUser = localStorage.getItem('reddi_user');
    
    if (token && savedUser) {
      try {
        // Set user immediately from localStorage to prevent UI flash
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        
        // Then validate with backend and update if needed
        const response = await authAPI.getMe();
        const freshUserData = {
          ...parsedUser,
          ...response.data,
        };
        setUser(freshUserData);
        // Update localStorage with fresh data
        localStorage.setItem('reddi_user', JSON.stringify(freshUserData));
      } catch (error) {
        console.error('Auth check failed:', error);
        // Only logout on 401, not on network errors
        if (error.response?.status === 401) {
          logout();
        }
      }
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, userType) => {
    const response = await authAPI.login({ email, password, user_type: userType });
    const { token, user_id, full_name } = response.data;
    
    localStorage.setItem('reddi_token', token);
    localStorage.setItem('reddi_user', JSON.stringify({ 
      id: user_id, 
      email, 
      full_name, 
      user_type: userType 
    }));
    
    setUser({ id: user_id, email, full_name, user_type: userType });
    setIsAuthenticated(true);
    
    return response.data;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user_id, user_type } = response.data;
    
    localStorage.setItem('reddi_token', token);
    localStorage.setItem('reddi_user', JSON.stringify({ 
      id: user_id, 
      email: data.email, 
      full_name: data.full_name, 
      user_type 
    }));
    
    setUser({ id: user_id, email: data.email, full_name: data.full_name, user_type });
    setIsAuthenticated(true);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('reddi_token');
    localStorage.removeItem('reddi_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
